import React, { useState } from 'react';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal, ModalProps } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';
import { HoldingsTable } from '../HoldingsTable/HoldingsTable';
import { FormFields } from '../../../AccountPreferences/components/AccountForm/AccountForm.meta';
import { useAccountFormContext } from '../../../AccountPreferences/components/AccountForm/context/useAccountFormContext';

interface EditHoldingsModalProps extends ModalProps {
  holdings: FieldArrayWithId<FormFields, 'bankAccount.Positions', 'key'>[];
  availableAssetBuckets: OptionType<number>[];
  onEditHolding(holdingIndex: number): void;
  onDeleteHolding(holdingId: string): void;
}

export const EditHoldingsModal = ({
  holdings,
  availableAssetBuckets,
  onEditHolding,
  onDeleteHolding,
  ...rest
}: EditHoldingsModalProps) => {
  const formContext = useFormContext();

  const {
    options: {
      AllowAssetBucketingCustomization: allowAssetBucketingCustomization,
      AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    },
  } = useAccountFormContext();

  const [targetBucket, setTargetBucket] = useState<OptionType<number> | null>(null);

  const [selectedHoldings, setSelectedHoldings] = useState<string[]>([]);

  return (
    <Modal size="xl" {...rest}>
      <ModalHeader as="h1">Edit Holdings</ModalHeader>

      <ModalCloseButton onClick={rest.onRequestClose} />

      <Box px="24">
        {allowAssetBucketingCustomization && !assetBucketingByManagementStyle && (
          <Select
            label="Move the selected holdings to"
            isSearchable={false}
            options={availableAssetBuckets}
            value={
              availableAssetBuckets.find(option => option.value === targetBucket?.value) ?? null
            }
            onChange={v => setTargetBucket(v)}
            css="width: 300px;"
            mb="32"
          />
        )}

        <Text fontWeight={600} mb="16">
          {holdings.length} Holdings
        </Text>
      </Box>

      <HoldingsTable
        availableAssetBuckets={availableAssetBuckets}
        onChange={setSelectedHoldings}
        holdings={holdings}
        onEditHolding={onEditHolding}
        onDeleteHolding={onDeleteHolding}
      />

      <ModalFooter gridGap="16">
        <Button variant="subtleInactive" ml="auto" onClick={rest.onRequestClose}>
          Cancel
        </Button>

        {allowAssetBucketingCustomization && !assetBucketingByManagementStyle && (
          <Button
            onClick={async ev => {
              try {
                if (targetBucket) {
                  const newHoldings = holdings.map(holding => ({
                    ...holding,
                    AssetBucketIndex: selectedHoldings.includes(holding.key)
                      ? targetBucket.value
                      : holding.AssetBucketIndex,
                  }));

                  formContext.setValue('bankAccount.Positions', newHoldings);

                  setTargetBucket(null);

                  if (rest.onRequestClose) {
                    rest.onRequestClose(ev);
                  }
                }
              } catch (err) {
                if (err instanceof Error) {
                  toast.error(err.message);
                }
              }
            }}
            disabled={targetBucket === null || selectedHoldings.length === 0}
          >
            Move Holding(s)
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
